import React, { useState } from "react"
import { Card, CardBody, Badge, UncontrolledDropdown, DropdownToggle, DropdownMenu, DropdownItem } from "reactstrap"
import Switch from "react-switch"
import { Vendor } from "./types"

interface VendorsTableProps {
  vendors: Vendor[]
  onToggleStatus: (vendor: Vendor) => void
  onVerify: (vendor: Vendor) => void
  onAdjustCommission: (vendor: Vendor) => void
  onViewDetails: (vendor: Vendor) => void
}

const VendorsTable: React.FC<VendorsTableProps> = ({
  vendors,
  onToggleStatus,
  onVerify,
  onAdjustCommission,
  onViewDetails
}) => {
  const [checkedVendors, setCheckedVendors] = useState<string[]>([])

  const handleCheckAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setCheckedVendors(vendors.map(v => v.id))
    } else {
      setCheckedVendors([])
    }
  }

  const handleCheckVendor = (vendorId: string) => {
    if (checkedVendors.includes(vendorId)) {
      setCheckedVendors(checkedVendors.filter(id => id !== vendorId))
    } else {
      setCheckedVendors([...checkedVendors, vendorId])
    }
  }

  const getVerificationBadgeColor = (verification: string) => {
    switch (verification) {
      case "Verified":
        return "success"
      case "Pending":
        return "warning"
      case "Not Verified":
        return "danger"
      default:
        return "secondary"
    }
  }

  return (
    <Card className="shadow-md rounded-3">
      <CardBody>
        <div className="table-responsive">
          <table className="table table-hover table-centered table-nowrap mb-0">
            <thead>
              <tr>
                <th style={{ width: "20px" }}>
                  <div className="form-check">
                    <input 
                      type="checkbox" 
                      className="form-check-input" 
                      onChange={handleCheckAll}
                      checked={checkedVendors.length === vendors.length && vendors.length > 0}
                    />
                  </div>
                </th>
                <th>Vendor</th>
                <th>Location</th>
                <th>Verification</th>
                <th>Orders</th>
                <th>Revenue Share</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {vendors.map((vendor) => (
                <tr key={vendor.id}>
                  <td>
                    <div className="form-check">
                      <input 
                        type="checkbox" 
                        className="form-check-input"
                        checked={checkedVendors.includes(vendor.id)}
                        onChange={() => handleCheckVendor(vendor.id)}
                      />
                    </div>
                  </td>
                  <td>
                    <div className="d-flex align-items-center">
                      <div className="avatar-sm rounded-circle me-2">
                        <img 
                          src={vendor.avatar} 
                          alt={vendor.name} 
                          className="rounded-circle"
                          style={{ width: "32px", height: "32px", objectFit: "cover" }}
                        />
                      </div>
                      <div>
                        <h5 className="font-size-14 mb-0 fw-medium">{vendor.name}</h5>
                        <p className="text-muted mb-0 font-size-12">{vendor.id}</p>
                      </div>
                    </div>
                  </td>
                  <td>{vendor.location}</td>
                  <td>
                    <Badge 
                      color={getVerificationBadgeColor(vendor.verification)}
                      className="d-inline-flex align-items-center"
                    >
                      <i className="bx bx-check-circle me-1"></i>
                      {vendor.verification}
                    </Badge>
                  </td>
                  <td>{vendor.orders}</td>
                  <td>{vendor.revenueShare}</td>
                  <td>
                    <Switch
                      onColor="#34c38f"
                      offColor="#f46a6a"
                      checked={vendor.status === "Active"}
                      onChange={() => onToggleStatus(vendor)}
                      height={20}
                      width={44}
                      uncheckedIcon={false}
                      checkedIcon={false}
                    />
                  </td>
                  <td>
                    <UncontrolledDropdown>
                      <DropdownToggle tag="a" className="text-muted font-size-18" style={{ cursor: "pointer" }}>
                        <i className="bx bx-dots-vertical-rounded"></i>
                      </DropdownToggle>
                      <DropdownMenu end>
                        <DropdownItem onClick={() => onVerify(vendor)}>
                          <i className="bx bx-check-circle me-2"></i>
                          Verify
                        </DropdownItem>
                        <DropdownItem onClick={() => onAdjustCommission(vendor)}>
                          <i className="bx bx-money me-2"></i>
                          Adjust Commission
                        </DropdownItem>
                        <DropdownItem onClick={() => onViewDetails(vendor)}>
                          <i className="bx bx-show me-2"></i>
                          View Details
                        </DropdownItem>
                      </DropdownMenu>
                    </UncontrolledDropdown>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardBody>
    </Card>
  )
}

export default VendorsTable
